import type { FileName } from "@pdfking/structs"

import { ResponseHeader } from "./ResponseHeader"

// The three spellings, in the order they win: the encoded `filename*`, which
// is the one ResponseCreate writes because a space, a quote or anything
// outside ASCII survives it; then the quoted and bare `filename` everyone else
// sends. Anything after a `;` is the next parameter, not part of the name.
const FILENAME = /filename\*=UTF-8''([^;]+)|filename="([^"]*)"|filename=([^;]+)/i

// A percent-escape that does not decode is not a name — say nothing and let
// the next spelling, or the caller's own name, answer instead.
const Decode = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  try {
    return decodeURIComponent(value)
  } catch {
    return undefined
  }
}

/**
 * What a response calls the thing it is answering with.
 *
 * The `content-disposition` header, which is where a name travels over the
 * wire — {@link ResponseCreate} writes it, this reads it back, so a stored
 * entry keeps its name across a download. Both spellings are read: the encoded
 * `filename*` a well-behaved writer sends, and the bare `filename` everyone
 * else does.
 *
 * @param r - Request, Response, or XMLHttpRequest object
 * @param name - What to call it when the response does not say
 * @returns The name from the header, or the default
 *
 * @example
 * ```ts
 * import { ResponseFileName } from "@pdfking/io"
 *
 * const filename = ResponseFileName(response, "download.pdf" as FileName)
 * // Returns "document.pdf" from "inline; filename=document.pdf"
 * ```
 */
export const ResponseFileName = (
  r: Request | Response | XMLHttpRequest,
  name: FileName = "" as FileName
): FileName => {
  const [, encoded, quoted, bare] =
    FILENAME.exec(ResponseHeader(r, "content-disposition", "")) ?? []

  return (Decode(encoded) ?? quoted ?? bare ?? name) as FileName
}
